import type { MarksheetTemplateId, MarksheetTemplateProjectData } from "services/marksheetTemplateBuilderService";

// ── Token reference ──────────────────────────────────────────────────────────
// Available tokens for use inside template HTML:
//
//  {{school.name}}           School name
//  {{school.code}}           School code
//  {{school.diseCode}}       DISE code
//  {{school.block}}          Block name
//  {{school.district}}       District name
//  {{exam.type}}             Exam type  (e.g. ANNUAL EXAMINATION)
//  {{session.year}}          Session year (e.g. 2025-26)
//  {{student.name}}          Student full name
//  {{student.rollNo}}        Roll number
//  {{student.enrollmentNo}}  Enrollment / admission number
//  {{student.fatherName}}    Father's name
//  {{student.motherName}}    Mother's name
//  {{student.dob}}           Date of birth (formatted DD-MM-YYYY)
//  {{student.class}}         Class + section (e.g. Class 6-A)
//  {{marksheet.percentage}}  Percentage (e.g. 78.40%)
//  {{marksheet.totalGrade}}  Overall grade
//  {{marksheet.result}}      PASS / FAIL
//  {{marksheet.rank}}        Class rank
//  {{marksheet.failSubjects}} Comma-separated failed subjects
//  {{marksTable}}            Full marks table HTML (auto-generated from subjects)
// ────────────────────────────────────────────────────────────────────────────

export const TOKEN_REFERENCE: Array<{ token: string; label: string }> = [
  { token: "{{school.name}}", label: "School Name" },
  { token: "{{school.code}}", label: "School Code" },
  { token: "{{school.diseCode}}", label: "DISE Code" },
  { token: "{{school.block}}", label: "Block" },
  { token: "{{school.district}}", label: "District" },
  { token: "{{exam.type}}", label: "Exam Type" },
  { token: "{{session.year}}", label: "Session Year" },
  { token: "{{student.name}}", label: "Student Name" },
  { token: "{{student.rollNo}}", label: "Roll No" },
  { token: "{{student.enrollmentNo}}", label: "Enrollment No" },
  { token: "{{student.fatherName}}", label: "Father's Name" },
  { token: "{{student.motherName}}", label: "Mother's Name" },
  { token: "{{student.dob}}", label: "Date of Birth" },
  { token: "{{student.class}}", label: "Class" },
  { token: "{{marksheet.percentage}}", label: "Percentage" },
  { token: "{{marksheet.totalGrade}}", label: "Total Grade" },
  { token: "{{marksheet.result}}", label: "Result" },
  { token: "{{marksheet.rank}}", label: "Rank" },
  { token: "{{marksheet.failSubjects}}", label: "Fail Subjects" },
  { token: "{{marksTable}}", label: "Marks Table (auto)" },
  { token: "{{student.photo}}", label: "Student Photo (auto)" },
];

export const baseCss = `
  * { box-sizing: border-box; }
  body { margin: 0; font-family: Arial, sans-serif; background: #fff; color: #000; }

  /* A4-like page */
  .ms-page {
    width: 210mm;
    max-width: 210mm;
    min-height: 297mm;
    margin: 10px auto;
    padding: 20px;
    position: relative;
    background: #fff;
    border: 1px solid #000;
    box-shadow: 0 0 0 1px #000;
  }

  /* Background watermark + subtle bands */
  .ms-bg { position: absolute; inset: 0; z-index: 0; pointer-events: none; }
  .ms-bands {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to right,
      rgba(144, 238, 144, 0.15) 0%,
      rgba(255, 255, 153, 0.15) 50%,
      rgba(255, 160, 160, 0.15) 100%
    );
  }
  .ms-watermark {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .ms-watermark-box {
    width: 60%;
    opacity: 0.08;
    border: 4px solid #000;
    padding: 18px;
    text-align: center;
    font-weight: 800;
    letter-spacing: 2px;
    text-transform: uppercase;
  }
  .ms-content { position: relative; z-index: 1; background: transparent; }

  /* Header */
  .ms-board-header {
    text-align: center;
    margin-bottom: 20px;
    border-bottom: 2px solid #000;
    padding-bottom: 15px;
  }
  .ms-school-name {
    font-size: 30px;
    font-weight: 800;
    margin: 5px 0 10px;
    text-transform: uppercase;
  }
  .ms-exam-title {
    font-size: 18px;
    font-weight: 800;
    margin: 0 0 5px;
    text-transform: uppercase;
  }
  .ms-dise { font-size: 14px; margin-top: 6px; }

  /* Tables */
  .ms-table {
    width: 100%;
    border-collapse: collapse;
    border: 1px solid #000;
  }
  .ms-table th,
  .ms-table td {
    border: 1px solid #000;
    padding: 8px 4px;
    text-align: center;
    font-size: 11px;
  }
  .ms-table th { background: #e9ecef; font-weight: 800; }
  .ms-table-wide th,
  .ms-table-wide td { padding: 10px 5px; }
  .ms-subject-cell { text-align: left !important; font-weight: 800; }
  .ms-total-row { background: #f8f9fa; }

  /* Student section */
  .ms-student-wrap { padding: 15px; }
  .ms-student-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 20px; }
  .ms-student-lines { flex: 1; }
  .ms-student-line { margin-bottom: 5px; font-size: 20px; font-weight: 800; }
  .ms-photo-wrap { display: flex; flex-direction: column; align-items: center; margin-left: 10px; margin-bottom: 20px; }
  .ms-photo-box {
    width: 130px;
    height: 155px;
    border: 2px solid #000;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 800;
    background: transparent;
  }
  .ms-student-photo {
    width: 130px;
    height: 155px;
    max-width: 130px;
    max-height: 155px;
    object-fit: cover;
    display: block;
  }
  /* When photo is inside the standard photo box, fill it cleanly without double border */
  .ms-photo-box .ms-student-photo {
    width: 100%;
    height: 100%;
    max-width: unset;
    max-height: unset;
  }

  /* Summary + signatures */
  .ms-summary { margin-top: 12px; }
  .ms-signatures { display: flex; justify-content: space-between; margin-top: 30px; gap: 12px; }
  .ms-sign { width: 48%; text-align: center; border-top: 2px solid #000; padding-top: 8px; font-weight: 800; }

  /* Token visual hint inside editor only */
  .ms-token {
    background: #dbeafe;
    color: #1e40af;
    padding: 0 3px;
    border-radius: 3px;
    font-size: 0.88em;
    font-style: italic;
  }

  /* Editor: blue placeholder. Print: resets when actual table rendered inside. */
  .ms-marks-table-token {
    margin: 12px 0;
    min-height: 40px;
    padding: 14px;
    background: #e8f4ff;
    border: 2px dashed #2563eb;
    text-align: center;
    border-radius: 4px;
  }
  .ms-marks-table-token::before {
    content: "MARKS TABLE";
    font-weight: 700;
    color: #1d4ed8;
    font-size: 13px;
    display: block;
    margin-bottom: 4px;
    font-style: italic;
  }
  .ms-marks-table-token:has(table) {
    padding: 0;
    background: transparent;
    border: none;
    min-height: 0;
    text-align: left;
  }
  .ms-marks-table-token:has(table)::before {
    content: none;
  }
`;

// ── Demo template ─────────────────────────────────────────────────────────────

function demoBodyHtml() {
  return `
    <div class="ms-page">
      <div class="ms-bg">
        <div class="ms-bands"></div>
        <div class="ms-watermark">
          <div class="ms-watermark-box">{{school.name}}</div>
        </div>
      </div>

      <div class="ms-content">
        <div class="ms-board-header">
          <div class="ms-school-name">{{school.name}}</div>
          <div class="ms-exam-title">{{exam.type}} {{session.year}}</div>
          <div class="ms-dise">DISE CODE: {{school.diseCode}} &nbsp;|&nbsp; SCHOOL CODE: {{school.code}}</div>
        </div>

        <table class="ms-table">
          <tbody>
            <tr>
              <th>SCHOOL CODE</th>
              <th>ENROLLMENT NUMBER</th>
              <th>REGULAR/PRIVATE</th>
              <th>ROLL NUMBER</th>
            </tr>
            <tr>
              <td>{{school.code}}</td>
              <td>{{student.enrollmentNo}}</td>
              <td>REGULAR</td>
              <td>{{student.rollNo}}</td>
            </tr>
          </tbody>
        </table>

        <div class="ms-student-wrap">
          <div class="ms-student-row">
            <div class="ms-student-lines">
              <div class="ms-student-line">STUDENT'S NAME: {{student.name}}</div>
              <div class="ms-student-line">FATHER'S NAME: {{student.fatherName}}</div>
              <div class="ms-student-line">MOTHER'S NAME: {{student.motherName}}</div>
              <div class="ms-student-line">DATE OF BIRTH: {{student.dob}}</div>
              <div class="ms-student-line">CLASS: {{student.class}}</div>
            </div>
            <div class="ms-photo-wrap">
              <div class="ms-photo-box">{{student.photo}}</div>
            </div>
          </div>
        </div>

        {{marksTable}}

        <div class="ms-summary">
          <table class="ms-table">
            <tbody>
              <tr>
                <th>PERCENTAGE</th>
                <th>TOTAL GRADE</th>
                <th>RESULT</th>
                <th>RANK</th>
                <th>FAIL SUBJECT</th>
              </tr>
              <tr>
                <td>{{marksheet.percentage}}%</td>
                <td>{{marksheet.totalGrade}}</td>
                <td>{{marksheet.result}}</td>
                <td>{{marksheet.rank}}</td>
                <td>{{marksheet.failSubjects}}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="ms-signatures">
          <div class="ms-sign">CLASS TEACHER</div>
          <div class="ms-sign">PRINCIPAL</div>
        </div>
      </div>
    </div>
  `;
}

// ── Blank template ────────────────────────────────────────────────────────────

const blankBodyHtml = `
  <div class="ms-page">
    <div class="ms-content">
      <div class="ms-board-header">
        <div class="ms-school-name">{{school.name}}</div>
        <div class="ms-exam-title">{{exam.type}}</div>
      </div>
      <div class="ms-student-wrap">
        <div class="ms-student-line">STUDENT: {{student.name}}</div>
        <div class="ms-student-line">CLASS: {{student.class}}</div>
      </div>
      {{marksTable}}
      <div class="ms-signatures">
        <div class="ms-sign">CLASS TEACHER</div>
        <div class="ms-sign">PRINCIPAL</div>
      </div>
    </div>
  </div>
`;

// ── Project data factories ────────────────────────────────────────────────────

function makeProjectData(bodyHtml: string): MarksheetTemplateProjectData {
  return {
    pages: [{ name: "Marksheet", component: bodyHtml }],
    styles: [{ selectors: [], style: {} }],
    _msCss: baseCss,
    _msHtml: bodyHtml,
  };
}

/** Returns project data seeded with the full demo marksheet (with tokens). */
export function getDemoProjectData(): MarksheetTemplateProjectData {
  return makeProjectData(demoBodyHtml());
}

/** Returns a minimal blank canvas project data. */
export function getBlankProjectData(): MarksheetTemplateProjectData {
  return makeProjectData(blankBodyHtml);
}

/** @deprecated Use getDemoProjectData or getBlankProjectData. Kept for runtime compatibility. */
export function getDefaultProjectData(_templateId?: MarksheetTemplateId): MarksheetTemplateProjectData {
  return getDemoProjectData();
}

// ── Extractors ────────────────────────────────────────────────────────────────

export function extractCssFromProjectData(data: MarksheetTemplateProjectData): string | null {
  const css = (data as any)?._msCss;
  return typeof css === "string" && css.trim().length > 0 ? css : baseCss;
}

export function extractHtmlFromProjectData(data: MarksheetTemplateProjectData): string | null {
  const html = (data as any)?._msHtml;
  return typeof html === "string" && html.trim().length > 0 ? html : null;
}
